import { useSyncExternalStore } from 'react'

export type Locale = 'is' | 'en' | 'pl'

export const LOCALES: readonly Locale[] = ['is', 'en', 'pl']

export const LOCALE_NAMES: Record<Locale, string> = {
  is: 'Íslenska',
  en: 'English',
  pl: 'Polski',
}

const INTL_TAGS: Record<Locale, string> = {
  is: 'is-IS',
  en: 'en-GB',
  pl: 'pl-PL',
}

const STORAGE_KEY = 'igf-locale'
const CHOICE_KEY = 'igf-locale-choice'

export function isLocale(value: unknown): value is Locale {
  return value === 'is' || value === 'en' || value === 'pl'
}

function readStored(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isLocale(stored) ? stored : null
  } catch {
    return null
  }
}

function readQuery(): Locale | null {
  if (typeof location === 'undefined') return null
  const requested = new URLSearchParams(location.search).get('lang')
  if (!isLocale(requested)) return null
  try {
    localStorage.setItem(STORAGE_KEY, requested)
  } catch {}
  markLocaleChoice(requested)
  return requested
}

export function markLocaleChoice(locale: Locale) {
  try {
    localStorage.setItem(CHOICE_KEY, locale)
  } catch {}
}

export function takeLocaleChoice(): Locale | null {
  try {
    const choice = localStorage.getItem(CHOICE_KEY)
    localStorage.removeItem(CHOICE_KEY)
    return isLocale(choice) ? choice : null
  } catch {
    return null
  }
}

function detect(): Locale {
  const fromQuery = readQuery()
  if (fromQuery) return fromQuery
  const stored = readStored()
  if (stored) return stored
  if (typeof navigator === 'undefined') return 'is'
  const languages = (navigator.languages?.length ? navigator.languages : [navigator.language]).map((l) =>
    (l ?? '').toLowerCase()
  )
  if (languages.some((l) => l.startsWith('is'))) return 'is'
  if (languages[0]?.startsWith('pl')) return 'pl'
  return 'is'
}

let current: Locale = detect()
const listeners = new Set<() => void>()

function applyDocumentLang(locale: Locale) {
  if (typeof document !== 'undefined') document.documentElement.lang = locale
}

applyDocumentLang(current)

export function getLocale(): Locale {
  return current
}

export function intlTag(locale: Locale = current): string {
  return INTL_TAGS[locale]
}

export function setLocale(next: Locale) {
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {}
  if (next === current) return
  current = next
  applyDocumentLang(next)
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale)
}
